import { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, Smartphone, Share, PlusSquare, Download, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { pushSupported, isIOS, isStandalone, getPushStatus, enablePush, disablePush, setPushTopics } from "@/lib/push";

function InstallBlock() {
  const [deferred, setDeferred] = useState(() => window.__installPrompt || null);
  const standalone = isStandalone();
  useEffect(() => {
    const h = () => setDeferred(window.__installPrompt || null);
    window.addEventListener("installprompt-ready", h);
    return () => window.removeEventListener("installprompt-ready", h);
  }, []);

  if (standalone)
    return (
      <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-sm text-emerald-700" data-testid="app-installed">
        <CheckCircle2 className="w-4 h-4 shrink-0" /> Appen er installeret på denne enhed
      </div>
    );

  return (
    <div className="rounded-xl border border-[#e9edf1] bg-white p-4 space-y-3" data-testid="install-block">
      <div className="flex items-center gap-2 font-semibold text-slate-900 text-sm">
        <Smartphone className="w-4 h-4 text-[#C8102E]" /> Installer som app
      </div>
      {deferred ? (
        <Button className="w-full bg-[#C8102E] hover:bg-[#a50d26] text-white rounded-lg" data-testid="install-app-btn"
          onClick={async () => {
            deferred.prompt();
            try { await deferred.userChoice; } catch { /* */ }
            window.__installPrompt = null; setDeferred(null);
          }}>
          <Download className="w-4 h-4 mr-2" /> Installer Lagersalg Stats
        </Button>
      ) : isIOS() ? (
        <ol className="text-sm text-slate-600 space-y-2">
          <li className="flex items-start gap-2"><span className="font-bold text-slate-900">1.</span> Åbn siden i <b>Safari</b></li>
          <li className="flex items-start gap-2"><span className="font-bold text-slate-900">2.</span> Tryk på <Share className="inline w-4 h-4 -mt-0.5" /> <b>Del</b> nederst</li>
          <li className="flex items-start gap-2"><span className="font-bold text-slate-900">3.</span> Vælg <PlusSquare className="inline w-4 h-4 -mt-0.5" /> <b>Føj til hjemmeskærm</b></li>
        </ol>
      ) : (
        <p className="text-sm text-slate-600">Åbn browserens menu (⋮) og vælg <b>Installer app</b> eller <b>Føj til startskærm</b>.</p>
      )}
    </div>
  );
}

function NotificationsBlock() {
  const [topics, setTopics] = useState([]);
  const [status, setStatus] = useState({ subscribed: false, topics: [] });
  const [busy, setBusy] = useState(true);
  const supported = pushSupported();
  const needsInstall = isIOS() && !isStandalone();

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/push/config");
      setTopics(data.topics || []);
      if (supported) setStatus(await getPushStatus());
    } catch { /* */ } finally { setBusy(false); }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleMain = async (on) => {
    setBusy(true);
    try {
      if (on) { setStatus(await enablePush(null)); toast.success("Notifikationer slået til"); }
      else { await disablePush(); setStatus({ subscribed: false, topics: [] }); toast.success("Notifikationer slået fra"); }
    } catch (e) {
      if (e?.message === "denied") toast.error("Notifikationer er blokeret. Tillad dem i telefonens indstillinger.");
      else toast.error("Kunne ikke slå notifikationer til");
    } finally { setBusy(false); }
  };

  const toggleTopic = async (id, on) => {
    const next = on ? [...new Set([...status.topics, id])] : status.topics.filter((t) => t !== id);
    setStatus((s) => ({ ...s, topics: next }));
    try { setStatus(await setPushTopics(next)); } catch { toast.error("Kunne ikke gemme"); refresh(); }
  };

  const test = async () => {
    try { await api.post("/push/test"); toast.success("Test-notifikation sendt"); setTimeout(refresh, 800); }
    catch { toast.error("Kunne ikke sende test"); }
  };

  return (
    <div className="rounded-xl border border-[#e9edf1] bg-white p-4 space-y-3" data-testid="notifications-block">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold text-slate-900 text-sm">
          {status.subscribed ? <Bell className="w-4 h-4 text-[#C8102E]" /> : <BellOff className="w-4 h-4 text-slate-400" />}
          Notifikationer på denne enhed
        </div>
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
          <Switch checked={status.subscribed} disabled={!supported || needsInstall || topics.length === 0}
            onCheckedChange={toggleMain} data-testid="push-toggle" />
        )}
      </div>
      {needsInstall && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          På iPhone virker notifikationer kun, når appen er føjet til hjemmeskærmen. Installer først, og åbn så appen fra ikonet.
        </p>
      )}
      {!supported && !needsInstall && (
        <p className="text-xs text-slate-500">Denne browser understøtter ikke notifikationer.</p>
      )}
      {topics.length === 0 && !busy && (
        <p className="text-xs text-slate-500">Der er endnu ingen notifikationer for din konto.</p>
      )}
      <p className="text-xs text-slate-500" data-testid="push-hint">
        Kommer der ingen besked? Tjek Indstillinger → Notifikationer → Lagersalg på telefonen, og at Fokus/Forstyr ikke er slået til.
      </p>
      {status.subscribed && (
        <div className="space-y-2 pt-1">
          {topics.map((t) => (
            <label key={t.id} className="flex items-center justify-between gap-3 text-sm text-slate-700" data-testid={`push-topic-${t.id}`}>
              {t.label}
              <Switch checked={status.topics.includes(t.id)} onCheckedChange={(v) => toggleTopic(t.id, v)} />
            </label>
          ))}
          {status.last && (
            <p className="text-xs text-slate-500" data-testid="push-last">
              Sidst sendt: {status.last.title} · {new Date(status.last.at).toLocaleString("da-DK", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
              {status.last.ok ? " · leveret til telefonens push-tjeneste" : ` · fejlede (${status.last.status ?? "ukendt"})`}
            </p>
          )}
          <Button variant="outline" size="sm" onClick={test} className="w-full rounded-lg mt-1" data-testid="push-test-btn">
            Send test-notifikation
          </Button>
        </div>
      )}
    </div>
  );
}

export function AppSettingsSheet({ open, onOpenChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl bg-[#F6F7F9] sm:max-w-lg sm:mx-auto max-h-[85vh] overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+1.5rem)]" data-testid="app-settings-sheet">
        <SheetHeader className="text-left mb-4">
          <SheetTitle>App & notifikationer</SheetTitle>
          <SheetDescription>Installer Lagersalg Stats på telefonen og vælg hvad du vil have besked om.</SheetDescription>
        </SheetHeader>
        <div className="space-y-3">
          <InstallBlock />
          <NotificationsBlock />
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function AppSettingsButton({ className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="icon" onClick={() => setOpen(true)} title="App & notifikationer"
        className={`h-9 w-9 rounded-lg bg-white border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] ${className}`} data-testid="app-settings-btn">
        <Bell className="w-4 h-4" />
      </Button>
      <AppSettingsSheet open={open} onOpenChange={setOpen} />
    </>
  );
}
